/**
 * @file /study — いちごスタディの入口 (家族選択)。
 *
 * @what: 所属 Family を一覧し、選んだ家族の `/study/{familyId}` へ進む。所属が 1 つだけなら
 *   選択画面を出さずに自動で進む (2026-08-19 代表判断)。
 * @why: web は app の「アクティブ家族」store を持たないため、URL に familyId を乗せて
 *   ページを家族スコープにする。以降の学習層 API は全て `/families/{familyId}/...`。
 * @failure: 一覧取得失敗は再読込を促す文言のみ (内部詳細は console)。所属 0 件は
 *   app 側で家族を作るよう案内する (web では家族を作らない)。
 */

import { Link, Navigate } from "react-router-dom";
import { familyLabel, roleLabel } from "@/features/study/api/families";
import { useMyFamilies } from "@/features/study/hooks/useMyFamilies";
import { useStudyBackground } from "@/features/study/hooks/useStudyBackground";
import "@/features/study/components/study.css";

export function StudyRoute() {
  const state = useMyFamilies();
  useStudyBackground();

  if (state.status === "loading") {
    return (
      <div
        className="study-scope"
        role="status"
        aria-live="polite"
        style={{ padding: "var(--space-5)" }}
      >
        家族を読み込んでいます...
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <section className="study-scope study-page">
        <h1>いちごスタディ</h1>
        <p role="alert" style={{ color: "var(--color-accent)" }}>
          家族の一覧を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      </section>
    );
  }

  const families = state.families;

  if (families.length === 1 && families[0]) {
    return <Navigate to={`/study/${encodeURIComponent(families[0].family_id)}`} replace />;
  }

  return (
    <section className="study-scope study-page">
      <h1>家族を選ぶ</h1>
      {families.length === 0 ? (
        <p className="study-muted">
          所属している家族がありません。いちご手帳アプリで家族を作成するか、招待を受けてから
          お使いください。
        </p>
      ) : (
        <>
          <p className="study-muted">どの家族を開きますか？</p>
          <ul className="study-family-list">
            {families.map((f) => (
              <li key={f.family_id}>
                <Link
                  className="study-family-card"
                  to={`/study/${encodeURIComponent(f.family_id)}`}
                >
                  <div className="study-family-name">{familyLabel(f)}</div>
                  <div className="study-family-meta">あなたの役割: {roleLabel(f.role)}</div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
